import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordRequirements, validatePassword } from "@/components/PasswordRequirements";
import { motion } from "framer-motion";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Neues Passwort – Englputzeder Tippspiel";
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return toast.error("Reset-Token fehlt");
    if (!validatePassword(password).ok) return toast.error("Passwort erfüllt nicht alle Anforderungen");
    if (password !== confirm) return toast.error("Passwörter stimmen nicht überein");
    setBusy(true);
    try {
      await api.auth.performReset(token, password);
      toast.success("Passwort geändert ✅");
      nav("/login", { replace: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Fehler beim Zurücksetzen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-hero text-primary-foreground flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm bg-card text-card-foreground rounded-lg shadow-pop p-6"
      >
        <h1 className="font-display font-extrabold text-2xl mb-1">Neues Passwort 🔐</h1>
        <p className="text-sm text-muted-foreground mb-4">
          {token ? "Lege jetzt dein neues Passwort fest." : "Bitte öffne den Link aus deiner E-Mail."}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <Input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Neues Passwort"
            className="h-12"
            disabled={!token}
          />
          {password.length > 0 && <PasswordRequirements password={password} />}
          <Input
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Passwort bestätigen"
            className="h-12"
            disabled={!token}
          />
          <Button
            type="submit"
            disabled={busy || !token}
            className="w-full h-12 bg-accent text-accent-foreground hover:bg-accent/90 font-bold tap"
          >
            {busy ? "Speichere …" : "Passwort speichern"}
          </Button>
        </form>
      </motion.div>
    </div>
  );
}
